import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { ButtonPrimary, ButtonSecondary } from 'components/Button'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import Modal from 'components/Modal'
import React, { useState } from 'react'
import { ArrowRight, X } from 'react-feather'
import styled, { useTheme } from 'styled-components'
import { ThemedText } from 'theme'

const Wrapper = styled.div`
  width: 100%;
  border-radius: 20px;
  max-width: 480px;
  max-height: 90vh;
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 8px;
`

const ModalTitle = styled(ThemedText.DeprecatedMediumHeader)`
  font-size: 16px;
  margin-bottom: 8px;
`

const ModalDescription = styled(ThemedText.DeprecatedBody)`
  font-size: 14px;
  color: ${({ theme }) => theme.textSecondary};
  text-align: center;
  margin-bottom: 16px;
`

const InputSection = styled.div`
  margin-bottom: 16px;
`

const InputLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

const StepsBox = styled.div`
  background: ${({ theme }) => theme.backgroundModule};
  border-radius: 12px;
  padding: 16px;
`

const StepItem = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`

const CloseIcon = styled.div`
  position: absolute;
  right: 20px;
  top: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 10;
  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.deprecated_bg2};
    opacity: 0.8;
  }
  @media (max-width: 768px) {
    right: 16px;
    top: 16px;
  }
`

const StyledButtonPrimary = styled(ButtonPrimary)`
  &:hover {
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
  }
  transition: all 0.2s ease;
  font-weight: 600;
  height: 48px;
  @media (max-width: 480px) {
    height: 44px;
  }
`

const StyledButtonSecondary = styled(ButtonSecondary)`
  &:hover {
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
  }
  transition: all 0.2s ease;
  font-weight: 600;
  height: 48px;
  @media (max-width: 480px) {
    height: 44px;
  }
`

const InfoBox = styled.div<{ type: 'benefits' | 'warning' }>`
  border-left: 3px solid ${({ theme, type }) => (type === 'benefits' ? theme.deprecated_green1 : theme.deprecated_red1)};
  background: ${({ theme, type }) =>
    type === 'benefits' ? `${theme.deprecated_green1}10` : `${theme.deprecated_red1}10`};
  padding: 10px 12px;
  border-radius: 4px;
  margin-bottom: 16px;
`

const InfoBoxTitle = styled(ThemedText.DeprecatedBody)<{ type: 'benefits' | 'warning' }>`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme, type }) => (type === 'benefits' ? theme.deprecated_green1 : theme.deprecated_red1)};
  margin-bottom: 6px;
`

const InfoBoxContent = styled(ThemedText.DeprecatedBody)`
  font-size: 12px;
`

const ButtonsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`

interface MigrateToAutoCompoundModalProps {
  isOpen: boolean
  onDismiss: () => void
  onConfirm: (amount: string) => void
  title?: string
  stakedAmount?: CurrencyAmount<Token>
  estimatedApr?: number
  estimatedApyWithAutoCompound?: number
  steps?: Array<{
    title: string
    description?: string
  }>
  warning?: string
  confirmText?: string
  cancelText?: string
}

const MigrateToAutoCompoundModal: React.FC<MigrateToAutoCompoundModalProps> = ({
  isOpen,
  onDismiss,
  onConfirm,
  title = 'Migrate to Auto-Compound',
  stakedAmount,
  estimatedApr,
  estimatedApyWithAutoCompound,
  steps,
  warning = 'Any unclaimed rewards in your current vault will not be automatically migrated. Make sure to claim them separately before or after migration.',
  confirmText = 'Start Migration',
  cancelText = 'Cancel',
}) => {
  const theme = useTheme()
  const [migrationAmount, setMigrationAmount] = useState('')

  // Default steps if not provided
  const defaultSteps = [
    {
      title: 'Withdraw your LP tokens from the Reward Vault',
      description: 'Your staked tokens will be returned to your wallet',
    },
    {
      title: 'Approve the Bault contract',
      description: 'This allows the Bault to access your LP tokens',
    },
    {
      title: 'Deposit your LP tokens into the Bault',
      description: 'Your LP tokens will be staked in the auto-compounding vault',
    },
  ]

  const stepsToShow = steps || defaultSteps

  // Handle max button click
  const handleMax = () => {
    if (stakedAmount) {
      setMigrationAmount(stakedAmount.toExact())
    }
  }

  // Handle confirm button click
  const handleConfirm = () => {
    onConfirm(migrationAmount)
  }

  // Reset input on modal close
  const handleDismiss = () => {
    setMigrationAmount('')
    onDismiss()
  }

  return (
    <Modal isOpen={isOpen} onDismiss={handleDismiss} maxHeight={90}>
      <Wrapper>
        <CloseIcon onClick={handleDismiss}>
          <X size={18} />
        </CloseIcon>

        <ModalContent>
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
            <ModalDescription>
              Migrate from the reward vault to an auto-compounding vault (Bault) to maximize your yield.
            </ModalDescription>
          </ModalHeader>

          <InputSection>
            <CurrencyInputPanel
              value={migrationAmount}
              onUserInput={setMigrationAmount}
              onMax={handleMax}
              showMaxButton={true}
              currency={stakedAmount?.currency}
              label=""
              hideBalance={false}
              renderBalance={() => stakedAmount?.toSignificant(6)}
              id="migration-amount-input"
              // disableCurrencySelect={true}
              // customBalanceText={`Available: ${stakedAmount?.toSignificant(6) || '0'}`}
            />
          </InputSection>

          {warning && (
            <InfoBox type="warning">
              <InfoBoxTitle type="warning">Important</InfoBoxTitle>
              <InfoBoxContent>{warning}</InfoBoxContent>
            </InfoBox>
          )}

          <ButtonsRow>
            <StyledButtonSecondary onClick={handleDismiss}>{cancelText}</StyledButtonSecondary>
            <StyledButtonPrimary onClick={handleConfirm} disabled={!migrationAmount || migrationAmount === '0'}>
              {confirmText}
              <ArrowRight size={16} style={{ marginLeft: '8px' }} />
            </StyledButtonPrimary>
          </ButtonsRow>
        </ModalContent>
      </Wrapper>
    </Modal>
  )
}

export default MigrateToAutoCompoundModal
